/**
 * `computeWorldBoundingBox`, the subtree world-space AABB that replaces
 * `THREE.Box3.setFromObject`. That prefers `SkinnedMesh.boundingBox`, a posed box
 * that a cloned GLTF skinned mesh holds far from the origin. Godot's editor also
 * selects by the mesh AABB, not the live pose.
 */
import * as THREE from 'three';
import { godotMatrixWorld } from '../resources/formats/glb/skinnedMeshSpace';
import { hasSurface } from '../godot/math/aabb';
import { copyAabb } from './visibilityRange/placements';
import { isEditorHelper, isNodeIcon } from './editorHelpers';
const _box = /*@__PURE__*/ new THREE.Box3();
const _matrix = /*@__PURE__*/ new THREE.Matrix4();
/**
 * Union of every descendant's world-space box, after a refresh of the world matrices: the drawn
 * geometry and the explicit AABB of a node ({@link godotAabbUserData}). Returns an empty box when
 * there is none. Editor chrome adds nothing, as the selection box of Godot unions `get_aabb` alone
 * (`node_3d_editor_plugin.cpp:5029-5065`). A node icon adds nothing either: its size follows the
 * camera, so it has no place in the scene.
 */
export function computeWorldBoundingBox(object, target = new THREE.Box3(), 
/** A descendant whose whole subtree is left out, such as another item's geometry. */
skipsSubtree) {
    target.makeEmpty();
    object.updateWorldMatrix(true, true);
    unionSubtree(object, target, skipsSubtree);
    return target;
}
function unionSubtree(object, target, skipsSubtree) {
    unionOwnBox(object, target);
    for (const child of object.children) {
        if (!isEditorHelper(child) && !skipsSubtree?.(child))
            unionSubtree(child, target, skipsSubtree);
    }
}
/** Where an object records that its node gives the editor no box of its own. */
const BOXLESS_KEY = 'godotBoxless';
/**
 * The userData of a draw whose node's `get_aabb` is `AABB()`, as a CPUParticles3D's is
 * (`cpu_particles_3d.cpp:43-45`): the selection box and the framing leave it out. On each draw, not
 * a group: the framing measures each drawable object on its own.
 */
export function boxlessUserData() {
    return { [BOXLESS_KEY]: true };
}
const AABB_KEY = 'godotAabb';
/**
 * The userData of an object that stands for the `get_aabb` of its node, in the node's space. A node
 * with a box and no drawn geometry of that size, such as a Decal, mounts it.
 */
export function godotAabbUserData(aabb) {
    return { [AABB_KEY]: aabb };
}
const FRAME_AABB_KEY = 'godotFrameAabb';
/**
 * The userData of an object that stands for the `get_aabb` of its node, in the node's space, for the
 * camera to frame on. Unlike {@link godotAabbUserData}, an empty box still frames, as the point it
 * is, and the selection and hover boxes leave it out.
 */
export function frameAabbUserData(aabb) {
    return { [FRAME_AABB_KEY]: aabb };
}
/** Writes the world box an object frames its node on to `target`, or returns null for an object that has none. */
export function frameBoxOf(object, target) {
    const aabb = object.userData[FRAME_AABB_KEY];
    if (!aabb)
        return null;
    object.updateWorldMatrix(true, false);
    return worldBoxOf(object, aabb, target);
}
/** Writes `aabb`, in the space of `object`, to `target` as a world box. */
function worldBoxOf(object, aabb, target) {
    copyAabb(target, aabb);
    return target.applyMatrix4(object.matrixWorld);
}
function unionExplicitBox(object, aabb, target) {
    if (!hasSurface(aabb.size))
        return;
    target.union(worldBoxOf(object, aabb, _box));
}
function unionOwnBox(object, target) {
    const explicit = object.userData[AABB_KEY];
    if (explicit) {
        unionExplicitBox(object, explicit, target);
        return;
    }
    const bearing = object;
    const geometry = bearing.geometry;
    if (!geometry || isNodeIcon(object) || object.userData[BOXLESS_KEY] === true)
        return;
    // An instanced draw's own box unions every instance and is not posed: an InstancedMesh, a
    // BatchedMesh, or points over an instanced geometry (`instancesBounds`). Without it a GridMap
    // frames one tile. Any other mesh uses the bind-pose `geometry.boundingBox`.
    const isInstanced = bearing.isInstancedMesh ||
        bearing.isBatchedMesh ||
        geometry.isInstancedBufferGeometry;
    const src = isInstanced ? bearing : geometry;
    if (src.boundingBox === null)
        src.computeBoundingBox?.();
    if (!src.boundingBox)
        return;
    _box.copy(src.boundingBox).applyMatrix4(godotMatrixWorld(object, _matrix));
    target.union(_box);
}
//# sourceMappingURL=bounds.js.map